<template>
  <div class="full-height">
    <div class="position--relative">
      <transition name="fade">
        <v-progress-linear
          v-if="isLoading"
          class="progress-linear--absolute"
          color="warning"
          height="3"
          indeterminate
        />
      </transition>
    </div>

    <transition name="fade">
      <div
        v-if="isSuccess"
        :class="{ 'full-height': !items.length }"
      >
        <div v-if="items.length" class="table">
          <div class="table__status">
            <strong>{{ firstItemNumber }}</strong>-<strong>{{ lastItemNumber }}</strong> {{ $t('Common.of') }} <strong>{{ pagination.totalElements | formatNumber }}</strong>&nbsp;<span class="text--lowercase">{{ $t('Common.costCenters') }}</span>
          </div>

          <v-layout class="table__header" align-center>
            <v-flex xs4>{{ $t('Common.costCenter') | capitalizeFirstLetter }}</v-flex>
            <v-flex xs2>{{ $t('Common.racing') | capitalizeFirstLetter }}</v-flex>
            <v-flex xs2>{{ $t('Common.amount') | capitalizeFirstLetter }}</v-flex>
            <v-flex xs2>
              {{ $t('Common.budget') | capitalizeFirstLetter }} {{ $t('Common.and') }} {{ $t('Common.extra') | capitalizeFirstLetter }}
            </v-flex>
            <v-flex xs2>{{ $t('Common.balance') | capitalizeFirstLetter }}</v-flex>
          </v-layout>

          <v-list subheader two-line class="table__body">
            <div v-for="(item, i) in items" :key="`tr-${i}`">
              <v-list-tile
                class="table__body__tr"
                :class="{ 'table__body__tr--active': activateLine(item) }"
                @click="handleClick($event, item)"
              >
                <v-list-tile-content>
                  <v-list-tile-sub-title>
                    <v-layout align-center>
                      <v-flex xs4>
                        <span
                          v-if="item.name.length > 3"
                          :title="item.name"
                          class="text--ellipsis"
                        >
                          {{ item.name }}
                        </span>
                        <span
                          v-else
                          :title="item.name"
                          class="text--ellipsis"
                        >
                          {{ item.name }}
                        </span>
                      </v-flex>

                      <v-flex xs2>
                        <span :title="item.amount">{{ item.amount }}</span>
                      </v-flex>

                      <v-flex xs2>
                        <span :title="item.totalFare | currencyBRL">
                          {{ item.totalFare | currencyBRL }}
                          <br>
                          <span class="grey--text text--darken-1 caption">
                            {{$t('Common.average')}} {{ item.averageFare | currencyBRL }}/{{$t('Common.ride')}}
                          </span>
                        </span>
                      </v-flex>

                      <v-flex xs2>
                        <span v-if="item.budgetMonthlyLimit">
                          <span :title="item.budgetMonthlyLimit | currencyBRL">
                            {{ item.budgetMonthlyLimit | currencyBRL }}
                          </span><br v-if="claims.costCenter['ROLE_PUT_COSTCENTER_ID']">
                          <span v-if="claims.costCenter['ROLE_PUT_COSTCENTER_ID']" class="grey--text text--darken-1 caption">
                            <router-link
                              v-if="item.totalFare / (item.budgetMonthlyLimit + extraMonthlyBudget(item.extraMonthlyBudget)) >= 0.85"
                              :to="{ name: 'CostCenter', params: { type: 'editar', id: item.id } }"
                              :title="$t('Common.setExtraBudget')"
                              class="text--link accent--text"
                            >
                              {{ $t('Common.setExtraBudget') }}
                            </router-link>
                            <span
                              v-else
                              :title="extraMonthlyBudget(item.extraMonthlyBudget) | currencyBRL"
                            >
                              {{ $t('Common.extraOf') }} {{ extraMonthlyBudget(item.extraMonthlyBudget) | currencyBRL }}
                            </span>
                          </span>
                        </span>
                        <span v-else-if="!claims.costCenter['ROLE_PUT_COSTCENTER_ID']">-</span>
                        <span v-else>
                          <router-link
                            :to="{ name: 'CostCenter', params: { type: 'editar', id: item.id } }"
                            :title="$t('Common.setBudget')"
                            class="text--link accent--text"
                          >
                            {{ $t('Common.setBudget') }}
                          </router-link>
                        </span>
                      </v-flex>

                      <v-flex xs2>
                        <span
                          v-if="item.budgetMonthlyLimit"
                          :title="(item.budgetMonthlyLimit + extraMonthlyBudget(item.extraMonthlyBudget)) - item.totalFare | currencyBRL"
                        >
                          {{ (item.budgetMonthlyLimit + extraMonthlyBudget(item.extraMonthlyBudget)) - item.totalFare | currencyBRL }}
                        </span>
                        <span
                          v-else
                          :title="$t('Common.noSetBudget')"
                          class="grey--text text--darken-1"
                        >
                          {{ $t('Common.noSetBudget') }}
                        </span>
                      </v-flex>
                    </v-layout>
                  </v-list-tile-sub-title>
                </v-list-tile-content>
              </v-list-tile>

              <v-divider />
            </div>
          </v-list>
        </div>

        <EmptyState
          v-else
          image="cost-center"
          :text="$t(`${namespace}.costCentroNotFound`)"
          style="padding-bottom: 120px"
        />

        <div v-if="pagination.totalElements > pagination.itemsPerPage" class="text-xs-center">
          <v-container class="px-0">
            <v-pagination
              v-model="pagination.currentPage"
              color="accent"
              :total-visible="totalVisible"
              :length="pagination.totalPages"
              @input="handlePage"
            />
          </v-container>
        </div>
      </div>
    </transition>

    <transition name="fade">
      <v-layout v-if="isFailed" align-center justify-center column fill-height>
        {{ $t('Common.genericErrorMsg') }}<br>
        <a
          :title="$t('Common.tryAgain')"
          class="accent--text"
          style="margin-bottom: 120px"
          @click.prevent="handleReloadData"
        >
          {{ $t('Common.tryAgain') }}.
        </a>
      </v-layout>
    </transition>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import { isArray, formatNumber } from 'utils/functions'
import EmptyState from 'components/EmptyState'

export default {
  name: 'Table',
  props: {
    items: {
      type: Array,
      required: true
    },
    pagination: {
      type: Object,
      required: true
    },
    status: {
      type: Number,
      default: STATUS_IS_LOADING
    },
    clearSelectedItem: {
      type: Boolean
    }
  },
  components: {
    EmptyState
  },
  computed: {
    ...mapState({
      selectedCompany: ({ company }) => company.selectedCompany,
      claims: ({ userClaims }) => userClaims.claims
    }),

    routeId () {
      const { id } = this.$route.params
      return id ? Number(id) : null
    },

    totalVisible () {
      return window.innerWidth <= 420 ? 5 : 7
    },

    firstItemNumber () {
      const { currentPage, itemsPerPage } = this.pagination
      const calc = (currentPage * itemsPerPage) - itemsPerPage + 1
      const item = currentPage === 1 ? 1 : calc
      return formatNumber(item)
    },

    lastItemNumber () {
      const { currentPage, itemsPerPage, totalElements } = this.pagination
      const calc = currentPage * itemsPerPage
      const item = calc > totalElements ? totalElements : calc
      return formatNumber(item)
    },

    isLoading () {
      return this.status === STATUS_IS_LOADING
    },

    isSuccess () {
      return this.status === STATUS_SUCCESS
    },

    isFailed () {
      return this.status === STATUS_FAILED
    }
  },
  data () {
    return {
      namespace: 'Views.ReportsPage',
      selectedItem: null
    }
  },
  methods: {
    ...mapActions({
      showAlert: 'snackbar/show'
    }),

    isValidArray (val) {
      return isArray(val)
    },

    extraMonthlyBudget (val) {
      return !val ? 0 : val
    },

    handlePage (page) {
      this.$emit('goToPage', page)
    },

    handleReloadData () {
      this.$emit('reloadData')
    },

    activateLine (item) {
      return item.id === this.selectedItem
    },

    handleClick ($event, item) {
      const cls = $event.target.classList

      if (
        !cls.contains('v-btn__content') &&
        !cls.contains('v-icon') &&
        !cls.contains('v-input--selection-controls__ripple') &&
        !cls.contains('accent--text')
      ) {
        this.selectedItem = item.id
        this.$emit('openDrawer', item)
      }
    }
  },
  watch: {
    clearSelectedItem () {
      this.selectedItem = null
    }
  }
}
</script>
